import React, { useState } from "react";
import { TableRow, TableCell, Table } from "../../../design/table";
import { useParticipant } from "../../../contexts/ParticipantContext";
import { HolidayRule } from "../../../models/HolidayRule";
import moment from "moment";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
} from "../../../design/dropdown";
import { Subheading } from "../../../design/heading";
import {
  EllipsisHorizontalIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import HolidayOverrideDialog from "../../../components/Dialogs/HolidayOverrideDialog/HolidayOverrideDialog";
import { getParticipantIdForHolidayRule } from "../../../helpers/getParticipantIdForHolidayRule";
import { getHolidayDateRangeForRule } from "../../../helpers/getHolidayDateRangeForRule";
import { Tooltip } from "../../../design/tooltip";

interface HolidayRuleLineItemProps {
  value: HolidayRule;
  year: number;
}

const HolidayRuleLineItem: React.FC<HolidayRuleLineItemProps> = ({
  value,
  year,
}) => {
  const { participants } = useParticipant();

  const [overrideDialogIsOpen, setOverrideDialogIsOpen] = useState(false);

  const { startDate, endDate } = getHolidayDateRangeForRule(value, year);
  const existingOverride = value.holidayOverrides?.find((o) => o.year === year);
  const participant = getParticipantIdForHolidayRule(value, year);

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Subheading>{value.name}</Subheading>
          {existingOverride && (
            <Tooltip content={existingOverride.reason ?? "No reason provided"}>
              <InformationCircleIcon className="w-4 h-4 text-blue-500 cursor-pointer" />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>{moment(startDate).format("MMMM D, YYYY h:mm A")}</TableCell>
      <TableCell>{moment(endDate).format("MMMM D, YYYY h:mm A")}</TableCell>
      <TableCell>
        {moment.duration(moment(endDate).diff(moment(startDate))).humanize()}
      </TableCell>
      <TableCell>
        {participants.find((p) => p._id === participant)?.name || "N/A"}
      </TableCell>
      <TableCell>
        <HolidayOverrideDialog
          isOpen={overrideDialogIsOpen}
          setIsOpen={setOverrideDialogIsOpen}
          holidayRule={value}
          year={year}
          existingOverride={existingOverride}
        />
        <Dropdown>
          <DropdownButton plain>
            <EllipsisHorizontalIcon className="w-5 h-5" />
          </DropdownButton>
          <DropdownMenu>
            <DropdownSection>
              <DropdownItem onClick={() => setOverrideDialogIsOpen(true)}>
                {existingOverride ? "Edit Override" : "Add Override"}
              </DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </TableCell>
    </TableRow>
  );
};

export default HolidayRuleLineItem;
